import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square } from "lucide-react";

/**
 * ReadAloud — browser Web Speech API TTS.
 *
 * If speechSynthesis is unavailable, we render an honest "not supported"
 * message. We NEVER simulate speech.
 */
export function ReadAloud({ text, testidPrefix = "tts" }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [rate, setRate] = useState(1.0);
  const utterRef = useRef(null);

  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
    };
  }, [supported]);

  if (!supported) {
    return (
      <p data-testid={`${testidPrefix}-unsupported`} className="text-xs text-fg-secondary">
        Read-aloud is not supported in this browser.
      </p>
    );
  }

  const play = () => {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = rate;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  };

  const pause = () => {
    window.speechSynthesis.pause();
    setState("paused");
  };
  const resume = () => {
    window.speechSynthesis.resume();
    setState("playing");
  };
  const stop = () => {
    window.speechSynthesis.cancel();
    setState("idle");
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-controls`}>
      {state === "idle" && (
        <Button
          size="sm"
          variant="outline"
          onClick={play}
          data-testid={`${testidPrefix}-play`}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          <Play className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Read aloud
        </Button>
      )}
      {state === "playing" && (
        <Button
          size="sm"
          variant="outline"
          onClick={pause}
          data-testid={`${testidPrefix}-pause`}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          <Pause className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Pause
        </Button>
      )}
      {state === "paused" && (
        <Button
          size="sm"
          variant="outline"
          onClick={resume}
          data-testid={`${testidPrefix}-resume`}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          <Play className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Resume
        </Button>
      )}
      {state !== "idle" && (
        <Button
          size="sm"
          variant="outline"
          onClick={stop}
          data-testid={`${testidPrefix}-stop`}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          <Square className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Stop
        </Button>
      )}
      <select
        value={rate}
        onChange={(e) => setRate(parseFloat(e.target.value))}
        data-testid={`${testidPrefix}-rate`}
        aria-label="Text-to-speech playback speed"
        className="text-xs bg-bg-base border border-border-subtle rounded px-2 py-1 text-fg-primary"
      >
        <option value={0.75}>0.75×</option>
        <option value={1}>1×</option>
        <option value={1.25}>1.25×</option>
      </select>
      <span className="text-xs text-fg-secondary" data-testid={`${testidPrefix}-label`}>
        device / browser-generated speech
      </span>
    </div>
  );
}

export default ReadAloud;
